/**
 * Downloads a declaration as the XML FinanzOnline takes in its upload (`lib/at/finanzonline.ts`).
 * What FinanzOnline would refuse is listed instead of a download, so a person never uploads a file
 * that comes back rejected.
 */
import { Download, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { FilingProblem } from "@/lib/at/finanzonline";
import { downloadFile } from "@/lib/export/tables";
import { track } from "@/lib/platform/analytics";

export function FilingDialog({
  form,
  open,
  onOpenChange,
  fileName,
  needsParagraph = false,
  build,
}: {
  form: "u30" | "zm";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  /** Turnover exempt without input tax (020): the § 6 Abs 1 Ziffer is asked for. */
  needsParagraph?: boolean;
  build: (paragraph: string) => { xml: string; problems: FilingProblem[] };
}) {
  const { t } = useTranslation();
  const [paragraph, setParagraph] = useState("");
  const { xml, problems } = build(paragraph);

  function download() {
    downloadFile(fileName, new Blob([xml], { type: "application/xml" }));
    track("filing_downloaded", { form });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`filing-dialog-${form}`}>
        <DialogHeader>
          <DialogTitle>{t(`filing.${form}.title`)}</DialogTitle>
          <DialogDescription>{t(`filing.${form}.description`)}</DialogDescription>
        </DialogHeader>

        {needsParagraph && (
          <div className="space-y-1">
            <Label htmlFor="exempt-paragraph">{t("filing.paragraph")}</Label>
            <Input
              id="exempt-paragraph"
              className="w-24 num"
              value={paragraph}
              maxLength={4}
              onChange={(event) => setParagraph(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t("filing.paragraphHint")}</p>
          </div>
        )}

        {problems.length > 0 ? (
          <Alert data-testid="filing-problems">
            <TriangleAlert className="size-4" />
            <AlertTitle>{t("filing.problemsTitle")}</AlertTitle>
            <AlertDescription>
              <ul className="list-disc space-y-1 pl-4">
                {problems.map((problem) => (
                  <li key={problem}>
                    {t(`filing.problems.${problem}`)}
                    {problem === "tax_number" && (
                      <>
                        {" "}
                        <Link className="underline" to="/settings">
                          {t("filing.openSettings")}
                        </Link>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-sm text-muted-foreground">{t("filing.howToUpload")}</p>
        )}

        <DialogFooter>
          <Button
            onClick={download}
            disabled={problems.length > 0}
            data-testid={`download-${form}`}
          >
            <Download /> {t("filing.download")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
